import React, { useCallback, useEffect, useState } from "react";
import { addDays, format, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { lpErr, card, btn, ghost } from "@/components/portal/lp/lpApi";
import TimeRangePicker, { label12 } from "@/components/nds/windows/sch/TimeRangePicker";

// Shift times are always New York time (what the scheduler stores), whatever the phone's own timezone is.
const ET = new Intl.DateTimeFormat("en-GB", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const hhmm = (iso) => ET.format(new Date(iso));
const monday = (d) => startOfWeek(d, { weekStartsOn: 1 });

/** Whole work week on one screen: tick the days you work, tap or drag the shift on the time boxes, one Save.
 *  Staff mode (sit-down session) edits every work shift that week and starts on next week. */
export default function WorkWeekEditor({ api, rid, onSaved, staff = false }) {
  const [week, setWeek] = useState(() => (staff ? addDays(monday(new Date()), 7) : monday(new Date())));
  const [openDay, setOpenDay] = useState(null);
  const getPath = staff ? `/schedules/employment/${rid}` : "/portal/work-schedule";
  const putPath = staff ? `/residents/${rid}/work-schedule` : "/portal/work-schedule";
  const editable = (sh) => staff || sh.created_by_resident_id === rid;
  const [rows, setRows] = useState(null);
  const [locked, setLocked] = useState({});
  const [conflicts, setConflicts] = useState({});
  const [busy, setBusy] = useState(false);

  const fetchWeek = useCallback((w) => api.get(getPath, { week_start: format(w, "yyyy-MM-dd") }), [api]);
  const toRows = (d) => d.days.map((x) => {
    const mine = x.shifts.find(editable);
    return { date: x.date, on: !!mine, start: mine ? hhmm(mine.start_time) : "09:00", end: mine ? hhmm(mine.end_time) : "17:00" };
  });
  useEffect(() => {
    setRows(null); setConflicts({});
    fetchWeek(week).then((d) => {
      setRows(toRows(d));
      setLocked(Object.fromEntries(d.days.map((x) => [x.date, x.shifts.filter((sh) => !editable(sh))])));
    }).catch((e) => toast.error(lpErr(e)));
  }, [week]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const copyLast = async () => {
    try {
      const prev = toRows(await fetchWeek(addDays(week, -7)));
      setRows((r) => r.map((x, i) => ({ ...x, on: prev[i].on, start: prev[i].start, end: prev[i].end })));
      toast.success(prev.some((x) => x.on) ? "Copied last week — tap Save" : "Last week had no shifts");
    } catch (e) { toast.error(lpErr(e)); }
  };
  const sameAsMonday = () => setRows((r) => r.map((x, i) => (i > 0 && i < 5 ? { ...x, on: r[0].on, start: r[0].start, end: r[0].end } : x)));
  const save = async () => {
    setBusy(true); setConflicts({});
    try {
      const out = await api.put(putPath, { week_start: format(week, "yyyy-MM-dd"), days: rows.filter((x) => x.on).map(({ date, start, end }) => ({ date, start, end })) });
      setConflicts(Object.fromEntries(out.conflicts.map((c) => [c.date, c.message])));
      if (out.conflicts.length) toast.error(`${out.conflicts.length} day(s) clash — change those times`); else { toast.success("Work week saved"); onSaved?.(); }
    } catch (e) { toast.error(lpErr(e)); } finally { setBusy(false); }
  };
  const hours = rows ? rows.filter((x) => x.on).reduce((a, x) => { const [sh, sm] = x.start.split(":").map(Number); const [eh, em] = x.end.split(":").map(Number); let h = eh + em / 60 - (sh + sm / 60); if (h <= 0) h += 24; return a + h; }, 0) : 0;

  return (
    <div className="space-y-3" data-testid="work-week-editor">
      <div className="flex items-center gap-2">
        <button type="button" className={ghost} onClick={() => setWeek(addDays(week, -7))} data-testid="work-week-prev" aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></button>
        <div className="flex-1 text-center text-sm font-semibold" data-testid="work-week-label">{format(week, "MMM d")} – {format(addDays(week, 6), "MMM d")}</div>
        <button type="button" className={ghost} onClick={() => setWeek(addDays(week, 7))} data-testid="work-week-next" aria-label="Next week"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="flex gap-2">
        <button type="button" className={`${ghost} flex-1 justify-center`} onClick={copyLast} data-testid="work-week-copy-last"><Copy className="h-3.5 w-3.5" /> Copy last week</button>
        <button type="button" className={`${ghost} flex-1 justify-center`} onClick={sameAsMonday} disabled={!rows} data-testid="work-week-same-as-monday">Mon hours → Tue–Fri</button>
      </div>
      {!rows ? <p className="text-sm text-white/50">Loading…</p> : (
        <div className={`${card} !p-2 divide-y divide-white/5`}>
          {rows.map((x, i) => (
            <div key={x.date} data-testid={`work-day-${x.date}`} className="py-2 px-1">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { set(i, { on: !x.on }); setOpenDay(x.on ? null : x.date); }} data-testid={`work-day-toggle-${x.date}`} aria-pressed={x.on}
                  className={`w-[64px] shrink-0 rounded-md py-2 text-xs font-semibold transition-colors ${x.on ? "bg-[#d4a017] text-black" : "bg-white/5 text-white/50"}`}>
                  {format(new Date(`${x.date}T12:00:00`), "EEE d")}
                </button>
                {x.on ? (
                  <button type="button" onClick={() => setOpenDay(openDay === x.date ? null : x.date)} data-testid={`work-day-time-${x.date}`}
                    className="flex-1 text-left rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm text-white">
                    {label12(x.start)} – {label12(x.end)} <span className="text-white/40 text-xs">· {openDay === x.date ? "pick on the boxes below" : "tap to change"}</span></button>
                ) : <span className="text-xs text-white/40">Off — tap the day if you work</span>}
              </div>
              {x.on && openDay === x.date && <div className="mt-2"><TimeRangePicker start={x.start} end={x.end} testid={`work-day-picker-${x.date}`} onChange={(a, b) => set(i, { start: a, end: b })} /></div>}
              {conflicts[x.date] && <p data-testid={`work-day-conflict-${x.date}`} className="mt-1 text-xs text-red-300 pl-[72px]">{conflicts[x.date]}</p>}
              {locked[x.date]?.map((s) => <p key={s.id} className="mt-1 text-[11px] text-white/40 pl-[72px]">Staff booked: {s.title} {hhmm(s.start_time)}–{hhmm(s.end_time)}</p>)}
            </div>
          ))}
        </div>
      )}
      <button type="button" className={`${btn} w-full`} onClick={save} disabled={busy || !rows} data-testid="work-week-save">{busy ? "Saving…" : `Save work week · ${Math.round(hours * 10) / 10} hrs`}</button>
    </div>
  );
}
